"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { BrainCircuit, Cloud, Workflow, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  title: "Comprehensive Safety Audits",
  description: "We identify hidden hazards before they become incidents. Our site-specific audits provide clear, actionable blueprints for risk mitigation and full regulatory adherence.",
  icon: BrainCircuit,
  size: "large",
  href: "/TheExpertPage",
  gradient: "from-primary/10 to-primary/5"
}, {
  title: "Compliance Programs",
  description: "Navigate complex OSHA and industry standards with ease. We develop custom safety manuals that integrate into your daily operations.",
  icon: Workflow,
  size: "medium",
  href: "/TheExpertPage",
  gradient: "from-chart-2/10 to-chart-2/5"
}, {
  title: "Training & Workshops",
  description: "Empower your workforce with proactive knowledge. Our interactive sessions transform safety culture from a requirement into a core value.",
  icon: Cloud,
  size: "medium",
  href: "/LetsTalkSafetyPage",
  gradient: "from-secondary/10 to-secondary/5"
}];
export default function ArchitectingChange() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="21742" id="core-safety-solutions" ref={ref} className={cn("bg-background text-foreground py-24 px-6 relative overflow-hidden", isVisible ? "opacity-100" : "opacity-0 transition-opacity duration-1000")}>
      {}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full max-w-7xl pointer-events-none">
        <div className="absolute top-20 left-20 w-96 h-96 bg-primary/5 rounded-full blur-[100px] opacity-30" />
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-secondary/5 rounded-full blur-[100px] opacity-30" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="mb-16 text-center max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-foreground to-foreground/80">
            Core Safety Solutions
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed font-sans">
            We provide the structural framework for organizational safety. From rigorous on-site audits to tailored training programs, we ensure your workplace remains compliant, secure, and productive.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 auto-rows-[minmax(300px,auto)]">
          {services.map((service, i) => <div key={service.title} data-index={i} className={cn("group relative overflow-hidden rounded-sm border border-border bg-card/90 backdrop-blur-sm p-8 flex flex-col justify-between transition-all duration-500", "hover:border-primary hover:shadow-xl hover:shadow-primary/5", service.size === "large" ? "lg:col-span-2 lg:row-span-2 min-h-[400px] lg:min-h-[500px]" : "lg:col-span-1 lg:row-span-1 min-h-[300px]")}>
              {}
              <div className={cn("absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br", service.gradient)} />

              {}
              <div className="relative z-10 flex flex-col h-full">
                <div className="mb-6">
                  <div className="inline-flex items-center justify-center p-3 rounded-sm bg-muted border border-border mb-6 text-primary group-hover:scale-110 transition-transform duration-300 ease-out">
                    <service.icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold mb-4 text-foreground tracking-tight">
                    {service.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed max-w-md font-sans">
                    {service.description}
                  </p>
                </div>

                {}
                <div className="mt-auto overflow-hidden pt-4">
                  <div className="transform translate-y-full opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300 ease-out">
                    <Link to={service.href} className="inline-flex items-center gap-2 text-primary font-bold hover:text-primary/80 transition-colors tracking-wide">
                      View Details
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}